import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Calendar,
  Building2,
  Users,
  Activity,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#8B1C4A', '#BC955C', '#235B4E', '#3F51B5', '#E91E63', '#4CAF50', '#FF9800', '#00BCD4'];

const formatDateToYMD = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const ResumenSemanal = () => {
  const { isAdmin, isEditor } = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [currentWeek, setCurrentWeek] = useState('');
  const [startOfTheWeek, setStartOfTheWeek] = useState('');
  const [downloadingWeekly, setDownloadingWeekly] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const currentDate = new Date();
        const dayOfTheWeek = currentDate.getDay();
        const diff = currentDate.getDate() - (dayOfTheWeek + 1);

        const weekStart = new Date(currentDate);
        weekStart.setDate(diff + (weekOffset * 7));

        const weekStartStr = formatDateToYMD(weekStart);

        setCurrentWeek(weekStartStr);
        setStartOfTheWeek(weekStart);

        const response = await axios.get(`${API}/summaries/city?week_start=${weekStartStr}`);
        setSummary(response.data);
      } catch (error) {
        console.error('Error fetching summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [weekOffset]);


  const renderPieLabel = ({ name, percent, cx, cy, midAngle, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + (isMobile ? 12 : 20);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#8B1C4A"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={isMobile ? 10 : 12}
        fontWeight={700}
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const formatWeekRange = (startOfTheWeek) => {
    if (!startOfTheWeek) return '';

    const start = new Date(startOfTheWeek);

    const end = new Date(start);
    end.setDate(end.getDate() + 7);

    const options = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('es-MX', options)} - ${end.toLocaleDateString('es-MX', options)}, ${start.getFullYear()}`;
  };

  const handleDownloadWeeklyPdf = async () => {
    try {
      setDownloadingWeekly(true);

      const response = await axios.get(
        `${API}/reports/weekly/dashboard-pdf`,
        {
          params: { reference_date: currentWeek },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/pdf',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resumen_semanal_${currentWeek}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading weekly PDF:', error);
      alert('No se pudo descargar el resumen semanal en PDF.');
    } finally {
      setDownloadingWeekly(false);
    }
  };

  const pieData = summary?.by_dependency?.slice(0, 8).map((d, i) => ({
    name: d.dependency?.acronym || 'Otro',
    value: d.beneficiarios,
    color: COLORS[i % COLORS.length]
  })) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando resumen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1]" data-testid="resumen-semanal">
      <section className="bg-white border-b border-[#D4C5A5]/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
                Resumen Semanal de Ciudad
              </h1>
              <p className="text-slate-600 mt-2">
                Consolidado del impacto del Gobierno de la CDMX
              </p>
            </div>

            <div className="flex items-center gap-4 bg-[#F5F0E1] rounded-lg p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setWeekOffset(weekOffset - 1)}
                className="hover:bg-white"
                data-testid="prev-week-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>

              <div className="flex items-center gap-2 px-4">
                <Calendar className="w-5 h-5 text-[#8B1C4A]" />
                <span className="font-medium text-slate-700">
                  {formatWeekRange(startOfTheWeek)}
                </span>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setWeekOffset(weekOffset + 1)}
                disabled={weekOffset >= 0}
                className="hover:bg-white"
                data-testid="next-week-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 -mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-[#8B1C4A] text-white border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-3xl font-bold">
                  {(summary?.summary?.total_beneficiarios || 0).toLocaleString()}
                </p>
                <p className="text-white/80 text-sm mt-1">Beneficiarios</p>
              </CardContent>
            </Card>

            <Card className="bg-[#BC955C] text-black border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-3xl font-bold">
                  {(summary?.summary?.total_acciones || 0).toLocaleString()}
                </p>
                <p className="text-black/70 text-sm mt-1">Acciones</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-[#D4C5A5]/30 shadow-lg">
              <CardContent className="p-6 text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 text-[#8B1C4A]" />
                <p className="text-3xl font-bold text-slate-800">
                  {summary?.summary?.total_activities || 0}
                </p>
                <p className="text-slate-500 text-sm mt-1">Actividades</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-[#D4C5A5]/30 shadow-lg">
              <CardContent className="p-6 text-center">
                <Building2 className="w-8 h-8 mx-auto mb-2 text-[#3F51B5]" />
                <p className="text-3xl font-bold text-slate-800">
                  {summary?.summary?.dependencies_active || 0}
                </p>
                <p className="text-slate-500 text-sm mt-1">Dependencias</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-[#D4C5A5]/30 shadow-lg">
              <CardContent className="p-6 text-center">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-[#235B4E]" />
                <p className="text-3xl font-bold text-slate-800">
                  {summary?.summary?.territories_covered || 0}
                </p>
                <p className="text-slate-500 text-sm mt-1">Territorios</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="border border-[#D4C5A5]/30">
              <CardHeader>
                <CardTitle>Distribución por Dependencia</CardTitle>
              </CardHeader>
              <CardContent>
               <div className="h-[220px] sm:h-[260px] lg:h-[300px] px-2 sm:pl-4 sm:pr-2" data-testid="distribution-chart">
  {pieData.length > 0 ? (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={pieData}
          dataKey="value"
          nameKey="name"
          cx={isMobile ? "50%" : "58%"}
          cy="50%"
          outerRadius={isMobile ? 58 : 72}
          label={isMobile ? false : renderPieLabel}
          labelLine={false}
        >
          {pieData.map((entry, index) => (
            <Cell key={index} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => value.toLocaleString()} />
      </PieChart>
    </ResponsiveContainer>
  ) : (
    <div className="h-full flex items-center justify-center text-slate-500">
      Sin datos esta semana
    </div>
  )}
</div>
              </CardContent>
            </Card>

            <div className="lg:col-span-2">
              <Card className="border border-[#D4C5A5]/30">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Ranking de Dependencias</span>
                    <Badge variant="secondary">{summary?.by_dependency?.length || 0} activas</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {summary?.by_dependency?.length > 0 ? (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                      {summary.by_dependency.map((item, index) => (
                        <Link
                          key={item.dependency?.id || index}
                          to={`/dependencia/${item.dependency?.id}`}
                          className="block"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-transparent hover:border-[#8B1C4A]/30 transition-all cursor-pointer">
                            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              >
                                {index + 1}
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800 break-words sm:truncate">
                                  {item.dependency?.name}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {item.dependency?.acronym}
                                </p>
                              </div>
                            </div>

                            <div className="flex gap-4 sm:gap-6 sm:ml-auto">
                              <div className="text-left sm:text-right sm:shrink-0">
                                <p className="font-bold text-[#8B1C4A]">
                                  {item.beneficiarios?.toLocaleString()}
                                </p>
                                <p className="text-xs text-slate-500">beneficiarios</p>
                              </div>

                              <div className="text-left sm:text-right sm:shrink-0">
                                <p className="font-bold text-[#BC955C]">
                                  {item.activities}
                                </p>
                                <p className="text-xs text-slate-500">actividades</p>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No hay actividades registradas esta semana</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {(isAdmin || isEditor) && (
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border border-[#D4C5A5]/30">
              <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Exportar resumen semanal
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Descarga el consolidado semanal correspondiente a la semana visible.
                  </p>
                </div>

                <Button
                  type="button"
                  className="bg-[#8B1C4A] hover:bg-[#70163A] w-full sm:w-auto"
                  onClick={handleDownloadWeeklyPdf}
                  disabled={downloadingWeekly || !currentWeek}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingWeekly ? 'Descargando...' : 'Descargar resumen semanal'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

export default ResumenSemanal;